import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Users, CheckCircle, Circle, MapPin, Globe } from "react-feather";

// Marked vs Not Marked, one figure in the middle. Ported from the Flutter
// dashboard's donut, and shared between the full Attendance Dashboard page and
// the dashboard box so the same numbers never render two different ways.
export const MARKED_GREEN = "#2E7D32";
export const NOT_MARKED_GREY = "#DDE1E6";
export const NOT_MARKED_RED = "#C62828";

// Nothing assigned is not 0% done - it is "no answer yet". A grey ring with no
// green in it says that; an empty chart area would look like a failed load.
const emptyRing = [{ name: "none", value: 1, color: NOT_MARKED_GREY }];

const AttendanceDonut = ({ marked = 0, notMarked = 0, total = 0, percent = 0, height = 170 }) => {
  const data =
    total === 0
      ? emptyRing
      : [
          { name: "Marked", value: marked, color: MARKED_GREEN },
          { name: "Not marked", value: notMarked, color: NOT_MARKED_GREY },
        ].filter((d) => d.value > 0);

  return (
    <div style={{ position: "relative", height }}>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius="66%"
            outerRadius="92%"
            paddingAngle={total === 0 ? 0 : 2}
            startAngle={90}
            endAngle={-270}
            isAnimationActive={false}
          >
            {data.map((entry) => (
              <Cell key={entry.name} fill={entry.color} stroke="none" />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      {/* Centred over the hole rather than inside the chart: recharts' own label
          would be re-laid-out on every resize, and this figure must not move. */}
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      >
        <div style={{ fontSize: height > 150 ? 28 : 22, fontWeight: 800, lineHeight: 1 }}>
          {Math.round(percent || 0)}%
        </div>
        <div className="text-muted" style={{ fontSize: 11 }}>Complete</div>
      </div>
    </div>
  );
};

// Total / Marked / Not Marked, the three figures the donut is a picture of.
export const AttendanceStatRow = ({ total = 0, marked = 0, notMarked = 0, compact = false }) => {
  const size = compact ? 15 : 18;
  const value = compact ? 14 : 17;
  const stats = [
    { Icon: Users, label: "Total", value: total, color: "#1B2A4A" },
    { Icon: CheckCircle, label: "Marked", value: marked, color: MARKED_GREEN },
    { Icon: Circle, label: "Not Marked", value: notMarked, color: NOT_MARKED_RED },
  ];

  return (
    <div className="d-flex justify-content-around text-center">
      {stats.map(({ Icon, label, value: v, color }) => (
        <div key={label}>
          <Icon size={size} color={color} />
          <div style={{ fontSize: value, fontWeight: 800, color }}>{v}</div>
          <div className="text-muted" style={{ fontSize: 11 }}>{label}</div>
        </div>
      ))}
    </div>
  );
};

/**
 * "My Areas" / "All Areas".
 *
 * `isScopedToMyAreas` is the server's own answer to "is this caller restricted" -
 * a plain supervisor sees only the areas they are assigned to, anyone holding
 * Attendance.View sees their whole branch scope. Without this chip the same
 * screen shows two very different totals with nothing to say which it is.
 */
export const ScopeChip = ({ isScopedToMyAreas }) => (
  <span className="badge bg-light text-muted d-inline-flex align-items-center gap-1" style={{ fontSize: 11 }}>
    {isScopedToMyAreas ? <MapPin size={12} /> : <Globe size={12} />}
    {isScopedToMyAreas ? "My Areas" : "All Areas"}
  </span>
);

export default AttendanceDonut;
